<template>
  <!-- Main Dashboard Container -->
  <div class="container-fluid py-4">
    <div v-if="allAvailableInsightsHidden" class="row">
      <div class="col-12">
        <div class="card dashboard-empty-insights-card border-0 shadow-sm">
          <div class="card-body text-center">
            <span class="dashboard-empty-insights-icon" aria-hidden="true">
              <AppIcon name="bi-eye-slash" class="bi bi-eye-slash" />
            </span>
            <h5 class="mt-3 mb-2">{{ $t('no_dashboard_insights_visible') }}</h5>
            <p class="text-muted mb-3">{{ $t('enable_dashboard_insights_description') }}</p>
            <button type="button" class="btn btn-primary" @click="openDashboardSettings">
              <AppIcon name="bi-sliders" class="bi bi-sliders me-2" aria-hidden="true" />
              {{ $t('open_dashboard_settings') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Inventory Summary -->
    <div class="row" v-if="helper.checkAccess('view_inventories') != -1 && dashboardSettings.show_products_overview">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0 text-secondary">{{$t('inventories_overview')}}</h5>
        <button class="btn btn-sm btn-link text-muted" @click="toggleSection('show_products_overview')" :title="$t('hide_section')">
          <AppIcon name="bi-eye-slash" class="bi bi-eye-slash" />
        </button>
      </div>


      <!-- Inventory Chart -->
      <div class="col-md-4 col-sm-12 mb-2">
        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border: 1px solid rgba(0,0,0,.125) !important;">
          <div class="card-body py-2 px-2" >
            <div class="d-flex align-items-center mb-1">
              <AppIcon name="bi-pie-chart-fill" class="bi bi-pie-chart-fill text-muted me-2" />
              <h6 class="text-muted mb-0 fw-semibold" style="font-size: 0.875rem;">{{$t('inventory_status')}}</h6>
            </div>
            <div v-if="loadingInventoryStats" class="d-flex justify-content-center align-items-center" style="height: 280px;">
              <div class="premium-spinner"></div>
            </div>
            <div v-show="!loadingInventoryStats && inventoryStats.totalProducts > 0">
              <div class="position-relative d-flex align-items-center justify-content-center" style="height: 240px;">
                <canvas ref="inventoryChart" style="width: 100%; height: 100%;"></canvas>
                <div class="position-absolute" style="top: 50%; left: 50%; transform: translate(-50%, -50%);">
                  <div class="text-center">
                    <h2 class="mb-0 fw-bold" style="color: #303030;">{{ formatValue(inventoryStats.totalProducts) }}</h2>
                    <small class="text-muted fw-semibold" style="font-size: 0.8rem;">{{$t('total')}}</small>
                  </div>
                </div>
              </div>
              <div class="mt-1">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span class="badge bg-success me-2" style="width: 12px; height: 12px; padding: 0;"></span>
                    <small class="text-muted">{{$t('in_stock')}}</small>
                  </div>
                  <small class="fw-bold" style="color: #28a745;">{{ formatValue(inventoryStats.totalProducts - inventoryStats.outOfStock) }}</small>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <span class="badge bg-danger me-2" style="width: 12px; height: 12px; padding: 0;"></span>
                    <small class="text-muted">{{$t('out_of_stock')}}</small>
                  </div>
                  <small class="fw-bold text-danger">{{ formatValue(inventoryStats.outOfStock) }}</small>
                </div>
              </div>
            </div>
            <div v-if="!loadingInventoryStats && inventoryStats.totalProducts === 0" class="text-center py-4 my-2 rounded" style="border: 1px dashed #dee2e6; height: 280px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
              <AppIcon name="bi-box-seam" class="bi bi-box-seam text-muted" style="font-size: 3rem;" />
              <h6 class="mt-3">{{ $t('no_products_added') }}</h6>
              <p class="text-muted small mb-0">{{ $t('add_products_to_see_insights') }}</p>
              <router-link to="/products" class="btn btn-sm btn-outline-primary mt-3">
                  {{ $t('go_to_products') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Out of Stock Products Table -->
      <div class="col-md-8 col-sm-12 mb-2">
        <div class="card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border: 1px solid rgba(0,0,0,.125) !important;">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <AppIcon name="bi-box" class="bi bi-box text-muted me-2" />
              <h6 class="text-muted mb-0 fw-semibold" style="font-size: 0.875rem;">{{$t('products')}} {{$t('out_of_stock')}}</h6>
            </div>
            <div class="table-responsive" style="max-height: 180px;background-color:rgb(255 255 255) !important;">
                  <table class="table table-sm" v-if="!loadingOutOfStock">
                    <thead>
                      <tr>
                        <th>{{$t('reference')}}</th>
                        <th>{{$t('label')}}</th>
                        <th>{{$t('quantity')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                        <tr v-for="product in products" :key="product.id">
                        <td><router-link :to="`/products/${product.id}`">{{product.reference}}</router-link></td>
                        <td>{{ product.label }}</td>
                        <td>{{ formatValue(product.inventories[0].quantity) }}</td>
                        </tr>
                    </tbody>
                  </table>
              <table-loading-bar v-if="loadingOutOfStock" class="py-4 my-2" />
              <div v-else-if="!products.length" class="text-center py-4 my-2 rounded" style="border: 1px dashed #dee2e6;">
                <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" style="font-size: 2rem;" />
                <h6 class="mt-2">{{ $t('all_products_in_stock') }}</h6>
                <p class="text-muted small mb-0">{{ $t('no_out_of_stock_products') }}</p>
              </div>
            </div>
            <div v-if="products.length && !loadingOutOfStock" class="d-flex justify-content-between align-items-center mt-2">
              <small class="text-muted">{{ formatValue(totalProducts) }} {{$t('products')}}</small>
              <nav aria-label="Page navigation">
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: currentProductPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="getOutOfStockProducts(currentProductPage - 1)" aria-label="Previous">
                      <span aria-hidden="true">&laquo;</span>
                    </a>
                  </li>
                  <li class="page-item" v-for="page in Math.ceil(totalProducts / 10)" :key="page" :class="{ active: currentProductPage === page }">
                    <a class="page-link" href="#" @click.prevent="getOutOfStockProducts(page)">{{ page }}</a>
                  </li>
                  <li class="page-item" :class="{ disabled: currentProductPage === Math.ceil(totalProducts / 10) }">
                    <a class="page-link" href="#" @click.prevent="getOutOfStockProducts(currentProductPage + 1)" aria-label="Next">
                      <span aria-hidden="true">&raquo;</span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- POS Overview -->
    <div class="row mt-8" v-if="helper.checkAccess('view_pos') != -1 && dashboardSettings.show_pos_overview">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0 text-muted">{{ $t('pos_overview') }}</h5>
        <button class="btn btn-sm btn-link text-muted" @click="toggleSection('show_pos_overview')" :title="$t('hide_section')">
          <AppIcon name="bi-eye-slash" class="bi bi-eye-slash" />
        </button>
      </div>

      <div
        v-for="metric in posOverviewCards"
        :key="metric.key"
        class="col-lg-4 col-md-6 col-sm-12 mb-3"
      >
        <div class="card insight-card pos-overview-card border-0 shadow-sm h-100" :style="{ '--pos-accent': metric.color }">
          <div class="card-body py-3">
            <div class="d-flex align-items-center">
              <div class="insight-icon me-3" :style="{ backgroundColor: metric.backgroundColor, color: metric.color }">
                <AppIcon :name="metric.icon" :class="metric.icon" class="fs-4" />
              </div>
              <div class="flex-grow-1">
                <small class="text-muted d-block mb-1 fw-semibold">{{ $t(metric.label) }}</small>
                <div v-if="loadingPosOverview" class="pos-overview-loading" aria-live="polite">
                  <div class="premium-spinner dashboard-mini-spinner"></div>
                </div>
                <h4 v-else class="mb-0 fw-bold" :style="{ color: metric.color }">
                  {{ formatMoney(posMetrics[metric.key]) }}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 mb-3">
        <div class="card border-0 shadow-sm pos-monthly-chart-card">
          <div class="card-body py-3">
            <div class="d-flex align-items-center mb-3">
              <div class="insight-icon-sm me-2 pos-monthly-chart-icon">
                <AppIcon name="bi-graph-up-arrow" class="bi bi-graph-up-arrow" />
              </div>
              <h6 class="text-muted mb-0 fw-semibold">{{ $t('pos_sales_this_month') }}</h6>
            </div>

            <div v-if="loadingPosOverview" class="pos-monthly-chart-state" aria-live="polite">
              <div class="premium-spinner"></div>
            </div>
            <div v-else-if="!posDailySales.length" class="pos-monthly-chart-state text-center">
              <div>
                <AppIcon name="bi-bar-chart" class="bi bi-bar-chart text-muted pos-monthly-empty-icon" />
                <p class="text-muted small mb-0 mt-2">{{ $t('no_data') }}</p>
              </div>
            </div>
            <div v-else class="pos-monthly-chart-wrap">
              <canvas ref="posMonthlySalesChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sales Overview -->
    <div class="row mt-8" v-if="helper.checkAccess('view_invoices') != -1 && dashboardSettings.show_sales_overview">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0 text-muted">{{$t('sales_overview')}}</h5>
          <button class="btn btn-sm btn-link text-muted" @click="toggleSection('show_sales_overview')" :title="$t('hide_section')">
            <AppIcon name="bi-eye-slash" class="bi bi-eye-slash" />
          </button>
        </div>

      <!-- Total Sales Card - Full Width -->
      <div class="col-12 mb-3">
        <div class="card insight-card border-0 shadow-sm" style="background-color: rgb(255 255 255) !important; border-left: 5px solid #303030 !important;">
          <div class="card-body py-3">
            <div class="row align-items-center">
              <div class="col-md-8">
                <div class="d-flex align-items-center">
                  <div class="insight-icon me-3" style="background-color: rgba(48, 48, 48, 0.1); color: #303030;">
                    <AppIcon name="bi-graph-up-arrow" class="bi bi-graph-up-arrow fs-4" />
                  </div>
                  <div>
                    <small class="text-muted d-block mb-1 fw-semibold">{{$t('total_sales')}}</small>
                    <h2 class="mb-0 fw-bold" style="color: #303030;">{{ formatMoney(salesMetrics.today) }}</h2>
                  </div>
                </div>
                <div class="dashboard-summary-chart mt-3">
                  <canvas ref="totalSalesChart" class="dashboard-mini-chart-canvas"></canvas>
                </div>
              </div>
              <div class="col-md-4 text-md-end mt-2 mt-md-0 d-flex flex-column align-items-end">
                <select v-model="durationTotalSales" class="form-select form-select-sm border-1 mb-2" style="max-width: 200px;">
                  <option value="today">{{$t('today')}}</option>
                  <option value="yesterday">{{$t('yesterday')}}</option>
                  <option value="last_7days">{{$t('last_7days')}}</option>
                  <option value="last_month">{{$t('last_month')}}</option>
                  <option value="last_3months">{{$t('last_3months')}}</option>
                  <option value="last_6months">{{$t('last_6months')}}</option>
                  <option value="this_year">{{$t('this_year')}}</option>
                  <option value="last_year">{{$t('last_year')}}</option>
                  <option value="custom">{{$t('custom_date')}}</option>
                </select>
                <div v-if="durationTotalSales === 'custom'" style="max-width: 200px;" class="w-100">
                   <date-picker :clearable="true" :editable="false" class="w-100" v-model="customRange" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Metrics Grid -->
      <div class="col-md col-sm-6">

        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #28a745 !important; cursor: pointer;" @click="navigateToInvoices('paid')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-muted bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              </div>
              <small class="text-muted fw-semibold">{{$t('paid_invoices')}}</small>
            </div>
            <h4 class="mb-1 text-success fw-bold">{{ formatMoney(totalPaidAmount) }}</h4>
            <small class="text-muted">{{ formatValue(totalPaidInvoices) }} {{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <div v-if="loadingDashboardCharts" class="dashboard-mini-chart-loading">
                <div class="premium-spinner dashboard-mini-spinner"></div>
              </div>
              <canvas
                v-show="!loadingDashboardCharts"
                ref="paidInvoicesChart"
                class="dashboard-mini-chart-canvas"
              ></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md col-sm-6">

        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #6c757d !important; cursor: pointer;" @click="navigateToInvoices('pending')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-clock-history" class="bi bi-clock-history" />
              </div>
              <small class="text-muted fw-semibold">{{$t('pending_invoices')}}</small>
            </div>
            <h4 class="mb-1 text-muted fw-bold">{{ formatMoney(totalPendingAmount) }}</h4>
            <small class="text-muted">{{ formatValue(totalPendingInvoices) }} {{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="pendingInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md col-sm-6">

        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #ffc107 !important; cursor: pointer;" @click="navigateToInvoices('partially_paid')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2 me-2">
                <AppIcon name="bi-exclamation-triangle-fill" class="bi bi-exclamation-triangle-fill" />
              </div>
              <small class="text-muted fw-semibold" style="font-size: 0.75rem;">{{$t('partially_invoices')}}</small>
            </div>
            <h5 class="mb-1 fw-bold">{{ formatValue(totalPartiallyPaidInvoices) }}</h5>
            <small class="text-muted">{{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="partiallyPaidInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md col-sm-6">

        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #dc3545 !important; cursor: pointer;" @click="navigateToInvoices('overdue')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-exclamation-octagon-fill" class="bi bi-exclamation-octagon-fill" />
              </div>
              <small class="text-muted fw-semibold">{{$t('overdue_invoices')}}</small>
            </div>
            <h4 class="mb-1 text-danger fw-bold">{{ formatMoney(dueStats.overdueAmount) }}</h4>
            <small class="text-muted">{{ formatValue(dueStats.overdueCount) }} {{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="overdueInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md col-sm-6">

        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #dc3545 !important; cursor: pointer;" @click="navigateToInvoices('canceled')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-x-circle-fill" class="bi bi-x-circle-fill" />
              </div>
              <small class="text-muted fw-semibold">{{$t('canceled_invoices')}}</small>
            </div>
            <h4 class="mb-1 text-danger fw-bold">{{ formatMoney(totalCanceledAmount) }}</h4>
            <small class="text-muted">{{ formatValue(totalCanceledInvoices) }} {{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="canceledInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Tables Section -->
      <div class="col-md-8 col-sm-12 mt-3">
        <div class="card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255)  !important ;border: 1px solid rgba(0,0,0,.125) !important;">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-muted me-2" />
              <h6 class="text-muted mb-0 fw-semibold" style="font-size: 0.875rem;">{{$t('due_invoices')}}</h6>
            </div>
            <div class="table-responsive" style="max-height: 220px;background-color:rgb(255 255 255) !important ">
                      <table class="table table-sm" v-if="!loadingDueInvoices">
                        <thead>
                          <tr>
                            <th>{{$t('reference')}}</th>
                            <th>{{$t('status')}}</th>
                            <th>{{$t('due_date')}}</th>
                            <th>{{$t('amount')}}</th>
                            <th>{{$t('amount_paid')}}</th>
                            <th>{{$t('outstanding')}}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="invoice in dueInvoices" :key="'due-'+invoice.id">
                            <td><router-link :to="`/invoices/${invoice.id}`">{{invoice.reference}}</router-link></td>
                            <td>
                              <span :class="{
                                'badge bg-warning': invoice.status === 'partially_paid',
                                'badge bg-danger': invoice.status === 'overdue',
                                'badge bg-secondary': invoice.status === 'pending'
                              }">{{ $t(invoice.status) }}</span>
                            </td>
                            <td>{{ formatDate(invoice.due_date) }}</td>
                            <td>{{ formatMoney(invoice.total) }}</td>
                            <td>{{ formatMoney(invoice.amount_paid) }}</td>
                            <td>{{ formatMoney(outstandingAmount(invoice)) }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <table-loading-bar v-if="loadingDueInvoices" class="py-4 my-2" />
                      <div v-else-if="!dueInvoices.length" class="text-center py-4 my-2 rounded" style="border: 1px dashed #dee2e6;">
                        <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" style="font-size: 2rem;" />
                        <h6 class="mt-2">{{ $t('no_due_invoices') }}</h6>
                        <p class="text-muted small mb-0">{{ $t('all_invoices_are_paid') }}</p>
                      </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4 col-sm-12 mt-3">
        <div class="card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255)  !important ;border: 1px solid rgba(0,0,0,.125) !important;">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <AppIcon name="bi-wallet2" class="bi bi-wallet2 text-primary me-2" />
              <h6 class="text-muted mb-0 fw-semibold" style="font-size: 0.875rem;">{{$t('sales_by_payment')}}</h6>
            </div>
            <div v-if="loadingPaymentBreakdown" class="d-flex justify-content-center align-items-center py-4 my-2">
              <div class="premium-spinner"></div>
            </div>
            <div v-else-if="paymentBreakdown.length" class="payment-breakdown">
              <div v-for="pm in paymentBreakdown" :key="'pm-'+pm.method" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="text-muted fw-semibold">{{ $t(pm.method) || $t('unknown') }}</small>
                  <span class="badge bg-secondary">{{ formatPercentage(pm.percent) }}</span>
                </div>
                <div class="progress" style="height: 10px;">
                  <div class="progress-bar" :class="getPaymentMethodColor(pm.method)" role="progressbar" :style="{width: pm.percent + '%'}" :aria-valuenow="pm.percent" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
                <small class="text-muted d-block mt-1">{{ formatMoney(pm.amount) }}</small>
              </div>
            </div>
            <div v-else class="text-center py-4 my-2 rounded" style="border: 1px dashed #dee2e6;">
              <AppIcon name="bi-wallet2" class="bi bi-wallet2 text-muted" style="font-size: 2rem;" />
              <h6 class="mt-2">{{ $t('no_data') }}</h6>
              <p class="text-muted small mb-0">{{ $t('no_sales_recorded_yet') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Supplier Invoices Overview -->
    <div class="row mt-8" v-if="helper.checkAccess('view_purchases') != -1 && dashboardSettings.show_purchases_overview">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0 text-secondary">{{$t('supplier_invoices_overview')}}</h5>
        <button class="btn btn-sm btn-link text-muted" @click="toggleSection('show_purchases_overview')" :title="$t('hide_section')">
          <AppIcon name="bi-eye-slash" class="bi bi-eye-slash" />
        </button>
      </div>

      <!-- Total Purchases Card - Full Width -->
      <div class="col-12 mb-3">
        <div class="card insight-card border-0 shadow-sm" style="background-color: rgb(255 255 255) !important; border-left: 5px solid #303030 !important;">
          <div class="card-body py-3">
            <div class="row align-items-center">
              <div class="col-md-8">
                <div class="d-flex align-items-center">
                  <div class="insight-icon me-3" style="background-color: rgba(48, 48, 48, 0.1); color: #303030;">
                    <AppIcon name="bi-cart-check" class="bi bi-cart-check fs-4" />
                  </div>
                  <div>
                    <small class="text-muted d-block mb-1 fw-semibold">{{$t('total_purchases')}}</small>
                    <h2 class="mb-0 fw-bold" style="color: #303030;">{{ formatMoney(totalPurchasesGross) }}</h2>
                  </div>
                </div>
                <div class="dashboard-summary-chart mt-3">
                  <canvas ref="totalPurchasesChart" class="dashboard-mini-chart-canvas"></canvas>
                </div>
              </div>
              <div class="col-md-4 text-md-end mt-2 mt-md-0 d-flex flex-column align-items-end">
                <select v-model="durationPurchases" class="form-select form-select-sm border-1 mb-2" style="max-width: 200px;">
                  <option value="today">{{$t('today')}}</option>
                  <option value="yesterday">{{$t('yesterday')}}</option>
                  <option value="last_7days">{{$t('last_7days')}}</option>
                  <option value="last_month">{{$t('last_month')}}</option>
                  <option value="last_3months">{{$t('last_3months')}}</option>
                  <option value="last_6months">{{$t('last_6months')}}</option>
                  <option value="this_year">{{$t('this_year')}}</option>
                  <option value="last_year">{{$t('last_year')}}</option>
                  <option value="custom">{{$t('custom_date')}}</option>
                </select>
                <div v-if="durationPurchases === 'custom'" style="max-width: 200px;" class="w-100">
                  <date-picker
                    v-model="customPurchaseRange"
                    :clearable="true"
                    :editable="false"
                    class="w-100"
                    type="date"
                    range
                    :multi-calendars="1"
                    :placeholder="$t('select_date_range')"
                  ></date-picker>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Metrics Grid -->
      <div class="col-md-4 col-sm-6">
        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #28a745 !important; cursor: pointer;" @click="navigateToPurchases('paid')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
              </div>
              <small class="text-muted fw-semibold">{{$t('paid_supplier_invoices')}}</small>
            </div>
            <h4 class="mb-0 fw-bold">{{ formatValue(totalPaidSupplierInvoices) }}</h4>
            <small class="text-muted">{{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="paidSupplierInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4 col-sm-6">
        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #6c757d !important; cursor: pointer;" @click="navigateToPurchases('pending')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-clock-history" class="bi bi-clock-history" />
              </div>
              <small class="text-muted fw-semibold">{{$t('pending_supplier_invoices')}}</small>
            </div>
            <h4 class="mb-0 fw-bold">{{ formatValue(totalPendingSupplierInvoices) }}</h4>
            <small class="text-muted">{{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="pendingSupplierInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4 col-sm-6">
        <div class="card insight-card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255) !important; border-left: 4px solid #dc3545 !important; cursor: pointer;" @click="navigateToPurchases('overdue')">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <div class="insight-icon-xs bg-secondary bg-opacity-10 text-secondary me-2">
                <AppIcon name="bi-exclamation-octagon-fill" class="bi bi-exclamation-octagon-fill" />
              </div>
              <small class="text-muted fw-semibold">{{$t('overdue_invoices')}}</small>
            </div>
            <h4 class="mb-1 text-danger fw-bold">{{ formatMoney(supplierDueStats.overdueAmount) }}</h4>
            <small class="text-muted">{{ formatValue(supplierDueStats.overdueCount) }} {{$t('invoices')}}</small>
            <div class="dashboard-mini-chart mt-2">
              <canvas ref="overdueSupplierInvoicesChart" class="dashboard-mini-chart-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Tables Section -->
      <div class="col-md-8 col-sm-12 mt-3">
        <div class="card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255)  !important ;border: 1px solid rgba(0,0,0,.125) !important;">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-danger me-2" />
              <h6 class="text-muted mb-0 fw-semibold" style="font-size: 0.875rem;">{{$t('supplier_due_invoices')}}</h6>
            </div>
            <div class="table-responsive" style="max-height: 220px;background-color:rgb(255 255 255)  !important ">
                  <table class="table table-sm" v-if="!loadingSupplierDueInvoices">
                    <thead>
                      <tr>
                        <th>{{$t('reference')}}</th>
                        <th>{{$t('status')}}</th>
                        <th>{{$t('due_date')}}</th>
                        <th>{{$t('amount')}}</th>
                        <th>{{$t('amount_paid')}}</th>
                        <th>{{$t('outstanding')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="invoice in supplierDueInvoices" :key="'sdue-'+invoice.id">
                        <td><router-link :to="`/supplier-invoices/${invoice.id}`">{{invoice.reference}}</router-link></td>
                        <td>
                          <span :class="{
                            'badge bg-warning': invoice.status === 'partially_paid',
                            'badge bg-danger': invoice.status === 'overdue',
                            'badge bg-secondary': invoice.status === 'pending'
                          }">{{ $t(invoice.status) }}</span>
                        </td>
                        <td>{{ formatDate(invoice.due_date) }}</td>
                        <td>{{ formatMoney(invoice.total) }}</td>
                        <td>{{ formatMoney(invoice.amount_paid) }}</td>
                        <td>{{ formatMoney(outstandingAmountSupplier(invoice)) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <table-loading-bar v-if="loadingSupplierDueInvoices" class="py-4 my-2" />
                  <div v-else-if="!supplierDueInvoices.length" class="text-center py-4 my-2 rounded" style="border: 1px dashed #dee2e6;">
                    <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" style="font-size: 2rem;" />
                    <h6 class="mt-2">{{ $t('no_due_invoices') }}</h6>
                    <p class="text-muted small mb-0">{{ $t('all_supplier_invoices_are_paid') }}</p>
                  </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4 col-sm-12 mt-3">
        <div class="card border-0 shadow-sm h-100" style="background-color:rgb(255 255 255)  !important ;border: 1px solid rgba(0,0,0,.125) !important;">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <AppIcon name="bi-wallet2" class="bi bi-wallet2 text-primary me-2" />
              <h6 class="text-muted mb-0 fw-semibold" style="font-size: 0.875rem;">{{$t('purchases_by_payment')}}</h6>
            </div>
            <div v-if="loadingSupplierPaymentBreakdown" class="d-flex justify-content-center align-items-center py-4 my-2">
              <div class="premium-spinner"></div>
            </div>
            <div v-else-if="supplierPaymentBreakdown.length" class="payment-breakdown">
              <div v-for="pm in supplierPaymentBreakdown" :key="'spm-'+pm.method" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="text-muted fw-semibold">{{ $t(pm.method) || $t('unknown') }}</small>
                  <span class="badge bg-secondary">{{ formatPercentage(pm.percent) }}</span>
                </div>
                <div class="progress" style="height: 10px;">
                  <div class="progress-bar" :class="getPaymentMethodColor(pm.method)" role="progressbar" :style="{width: pm.percent + '%'}" :aria-valuenow="pm.percent" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
                <small class="text-muted d-block mt-1">{{ formatMoney(pm.amount) }}</small>
              </div>
            </div>
            <div v-else class="text-center py-4 my-2 rounded" style="border: 1px dashed #dee2e6;">
              <AppIcon name="bi-wallet2" class="bi bi-wallet2 text-muted" style="font-size: 2rem;" />
              <h6 class="mt-2">{{ $t('no_data') }}</h6>
              <p class="text-muted small mb-0">{{ $t('no_purchases_recorded_yet') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!allAvailableInsightsHidden && helper.checkAccess('view_inventories') == -1 && helper.checkAccess('view_invoices') == -1 && helper.checkAccess('view_purchases') == -1 && helper.checkAccess('view_pos') == -1">
    <div class="row" v-if="modules?.length">  
      <h5 class="mb-3 text-secondary">{{$t('modules')}}</h5>
      <div
        v-for="module in modules?.filter(m => m.enabled)"
        :key="module.name"
        class="col-md-3 col-sm-6 mb-4"
      >
        <router-link :to="module.items[0].link || '#'" class="text-decoration-none">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex flex-column align-items-center justify-content-center">
              <AppIcon :name="module.icon + ' mb-2'" v-if="module.icon" :class="module.icon + ' mb-2'" style="font-size:2rem" />
              <h6 class="mb-1 text-center">{{ $t(module.name) }}</h6>
              <small class="text-dark text-center" v-if="module.description">{{ $t(module.description) }}</small>
            </div>
          </div>
        </router-link>
      </div>
    </div>
    </div>

  </div>
</template>

<script>

import moment from 'moment'
import helper from '../helper'
import Chart from 'chart.js'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'ERPDashboard',
  components: {
    DatePicker
  },
  data() {
    return {
      dashboardSettings: {
        show_products_overview: true,
        show_pos_overview: false,
        show_sales_overview: true,
        show_purchases_overview: true,
        hide_values: false
      },
      duration: 'last_3months',
      durationTotalSales: 'last_3months',
      durationInvoices: 'last_3months',
      durationPurchases: 'last_3months',
      durationUnpaidInvoices: 'last_3months',
      range: [],
      customRange: [],
      customPurchaseRange: [],
      products : [],
      invoices :[],
      dueInvoices: [],
      loadingInventoryStats: false,
      loadingOutOfStock: false,
      loadingDueInvoices: false,
      loadingPaymentBreakdown: false,
      loadingSupplierDueInvoices: false,
      loadingSupplierPaymentBreakdown: false,
      loadingPaidInvoicesTrend: false,
      loadingDashboardCharts: false,
      loadingPosOverview: false,
      invoiceStatusTrendRows: [],
      supplierStatusTrendRows: [],
      totalTransactions : 0,
      currentTransactionPage : 1,
      currentProductPage : 1,
      totalProducts : 0,
      inventoryStats: {
        totalProducts: 0,
        outOfStock: 0
      },
      totalPaidInvoices : 0,
      totalPartiallyPaidInvoices : 0,
      totalCanceledInvoices :0 ,
      totalPendingInvoices : 0,
      totalPaidAmount: 0,
      totalPendingAmount: 0,
      totalCanceledAmount: 0,
      dueStats: {
        overdueAmount: 0,
        overdueCount: 0,
        dueTodayAmount: 0,
        dueTodayCount: 0,
        due7Amount: 0,
        due7Count: 0,
      },
      arStats: {
        totalAR: 0,
        avgInvoiceValue: 0,
        paidRate: 0,
      },
      collectionStats: {
        totalPaidAmount: 0,
        totalInvoicesCount: 0,
      },
      paymentBreakdown: [],
      // Supplier invoices overview
      totalPurchasesGross: 0,
      totalPaidSupplierInvoices: 0,
      totalPendingSupplierInvoices: 0,
      supplierDueInvoices: [],
      supplierDueStats: {
        overdueAmount: 0,
        overdueCount: 0,
        dueTodayAmount: 0,
        dueTodayCount: 0,
        due7Amount: 0,
        due7Count: 0,
      },
      supplierPaymentBreakdown: [],
      salesMetrics: {
        today: 0,
        todayGrowth: 0,
        week: 0,
        weekGrowth: 0,
        month: 0,
        monthGrowth: 0
      },
      posMetrics: {
        today: 0,
        week: 0,
        month: 0
      },
      posDailySales: [],
      posMonthlySalesChart: null,
      inventoryChart: null,
      paidInvoicesChart: null,
      dashboardCharts: {},
    }
  },
  methods: {
    openDashboardSettings() {
      this.$router.push({
        path: '/settings/modules',
        query: { open: 'dashboard' }
      });
    },
    loadDashboardSettings() {
      const metadata = this.business?.metadata || {};
      this.dashboardSettings = {
        show_products_overview: true,
        show_pos_overview: false,
        show_sales_overview: true,
        show_purchases_overview: true,
        hide_values: false,
        ...(metadata.dashboard_settings || {})
      };
    },
    async toggleSection(sectionKey) {
      const result = await this.$swal.fire({
        text: this.$t('hide_section_confirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#004aad',
        confirmButtonText: this.$t('yes'),
        cancelButtonText: this.$t('cancel')
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const business = this.business;
        const metadata = { ...(business.metadata || {}) };

        this.dashboardSettings[sectionKey] = !this.dashboardSettings[sectionKey];
        metadata.dashboard_settings = {
          ...(metadata.dashboard_settings || {}),
          ...this.dashboardSettings
        };

        await helper.customPost(`${process.env.VUE_APP_API}/businesses`, {
          metadata: metadata
        });

        this.$store.commit('SET_BUSINESS', {
          ...business,
          metadata: metadata
        });

        const action = this.dashboardSettings[sectionKey] ? 'shown' : 'hidden';
        this.$toast.open({
          message: this.$t('section_' + action),
          type: 'success'
        });
      } catch (error) {
        this.dashboardSettings[sectionKey] = !this.dashboardSettings[sectionKey];
        this.$toast.open({
          message: this.$t('error_saving_settings'),
          type: 'error'
        });
      }
    },
    formatMoney(number) {
      return this.hideDashboardValues ? '******' : helper.formatMoney(number);
    },
    formatValue(value) {
      return this.hideDashboardValues ? '******' : value;
    },
    formatPercentage(value) {
      return this.hideDashboardValues ? '******' : `${Number(value || 0).toFixed(0)}%`;
    },
    navigateToInvoices(status) {
      const startDate = this.getStartDate(this.durationTotalSales);
      const endDate = this.getEndDate(this.durationTotalSales);
      this.$router.push({
        path: '/invoices',
        query: {
          status: status,
          start: startDate,
          end: endDate
        }
      });
    },
    navigateToPurchases(status) {
      const startDate = this.getStartDate(this.durationPurchases, this.customPurchaseRange);
      const endDate = this.getEndDate(this.durationPurchases, this.customPurchaseRange);
      this.$router.push({
        path: '/supplier-invoices',
        query: {
          status: status,
          start: startDate,
          end: endDate
        }
      });
    },
    formatNumber(value) {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },
    async getTotalPaidInvoies(status = 'paid'){
      const startDate = this.getStartDate(this.durationTotalSales);
      const endDate = this.getEndDate(this.durationTotalSales);
      const {
        data
        } = await helper.customGet(`${process.env.VUE_APP_API}/analytics/total?metric=invoices&start=${startDate}&end=${endDate}&status=${status}`)
      
      if(status == 'paid'){
        this.totalPaidInvoices = data.count
        this.totalPaidAmount = data.total || 0
      }else if(status == 'partially_paid'){
        this.totalPartiallyPaidInvoices = data.count
      }else if (status == 'pending'){
        this.totalPendingInvoices = data.count
        this.totalPendingAmount = data.total || 0
      }else if (status == 'canceled'){
        this.totalCanceledInvoices = data.count
        this.totalCanceledAmount = data.total || 0
      }
    },
    async fetchDashboardSummary() {
      const startDate = this.getStartDate(this.durationTotalSales);
      const endDate = this.getEndDate(this.durationTotalSales);

      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/dashboard/summary?start=${startDate}&end=${endDate}`,
          { silent: true }
        );
        const inventory = response?.data?.inventory || {};
        const sales = response?.data?.sales || {};
        const statuses = sales.statuses || {};
        const paid = statuses.paid || {};
        const pending = statuses.pending || {};
        const canceled = statuses.canceled || {};
        const partiallyPaid = statuses.partially_paid || {};

        this.inventoryStats = {
          totalProducts: Number(inventory.total_products || 0),
          outOfStock: Number(inventory.total_out_of_stock || 0)
        };
        this.salesMetrics.today = Number(sales.total_sales || 0);
        this.totalPaidInvoices = Number(paid.count || 0);
        this.totalPaidAmount = Number(paid.total || 0);
        this.totalPendingInvoices = Number(pending.count || 0);
        this.totalPendingAmount = Number(pending.total || 0);
        this.totalCanceledInvoices = Number(canceled.count || 0);
        this.totalCanceledAmount = Number(canceled.total || 0);
        this.totalPartiallyPaidInvoices = Number(partiallyPaid.count || 0);
        return true;
      } catch (error) {
        // Supports rolling deployments where the frontend may be published
        // before the additive API endpoint is available.
        return false;
      }
    },
    async refreshDashboardSummary() {
      if (await this.fetchDashboardSummary()) return;

      await Promise.allSettled([
        this.getTotalPaidInvoies('paid'),
        this.getTotalPaidInvoies('pending'),
        this.getTotalPaidInvoies('canceled'),
        this.getTotalPaidInvoies('partially_paid'),
        this.updateSingleMetric('total_sales', this.durationTotalSales)
      ]);
    },
    async fetchPaidInvoicesTrend() {
      this.loadingPaidInvoicesTrend = true;
      this.loadingDashboardCharts = true;
      try {
        const startDate = this.getStartDate(this.durationTotalSales);
        const endDate = this.getEndDate(this.durationTotalSales);
        const res = await helper.customGet(`${process.env.VUE_APP_API}/v2/analytics?type=invoices-status-trend&start=${startDate}&end=${endDate}`);
        const rows = res?.data?.data || [];
        this.invoiceStatusTrendRows = rows;
        this.loadingPaidInvoicesTrend = false;
        this.loadingDashboardCharts = false;
        this.$nextTick(() => {
          this.renderDashboardCharts();
        });
      } catch (e) {
        this.invoiceStatusTrendRows = [];
        this.$nextTick(() => {
          this.renderDashboardCharts();
        });
      } finally {
        this.loadingPaidInvoicesTrend = false;
        this.loadingDashboardCharts = false;
      }
    },
    
    async fetchMetric(metric, timeRange) {
        const startDate = this.getStartDate(timeRange);
        const endDate = this.getEndDate(timeRange);
        const response = await helper.customGet(`${process.env.VUE_APP_API}/home/insights?metric=${metric}&start=${startDate}&end=${endDate}`);
        return response?.data?.total ? response?.data?.total : 0;
    },
    async fetchPosOverview() {
      if (helper.checkAccess('view_pos') === -1 || !this.dashboardSettings.show_pos_overview) return;

      this.loadingPosOverview = true;
      const today = moment().format('YYYY-MM-DD');
      const ranges = {
        today: today,
        week: moment().startOf('isoWeek').format('YYYY-MM-DD'),
        month: moment().startOf('month').format('YYYY-MM-DD')
      };

      try {
        const responses = await Promise.all([
          ...Object.values(ranges).map(start => helper.customGet(
            `${process.env.VUE_APP_API}/pos/total-sales?start=${start}&end=${today}`,
            { silent: true }
          )),
          helper.customGet(
            `${process.env.VUE_APP_API}/pos/daily-sales-trend?start=${ranges.month}&end=${today}`,
            { silent: true }
          )
        ]);
        const keys = Object.keys(ranges);
        this.posMetrics = responses.slice(0, keys.length).reduce((metrics, response, index) => {
          metrics[keys[index]] = Number(response?.data?.total_sales || 0);
          return metrics;
        }, { today: 0, week: 0, month: 0 });
        this.posDailySales = Array.isArray(responses[keys.length]?.data)
          ? responses[keys.length].data
          : [];
      } catch (error) {
        this.posMetrics = { today: 0, week: 0, month: 0 };
        this.posDailySales = [];
      } finally {
        this.loadingPosOverview = false;
        this.$nextTick(() => this.renderPosMonthlySalesChart());
      }
    },
    renderPosMonthlySalesChart() {
      const canvas = this.$refs.posMonthlySalesChart;
      if (!canvas || !this.posDailySales.length) return;

      if (this.posMonthlySalesChart) {
        this.posMonthlySalesChart.destroy();
      }

      const totalsByDate = new Map(
        this.posDailySales.map(row => [
          moment(row.date).format('YYYY-MM-DD'),
          Number(row.total_sales || 0)
        ])
      );
      const labels = [];
      const totals = [];
      const cursor = moment().startOf('month');
      const today = moment().startOf('day');

      while (cursor.isSameOrBefore(today, 'day')) {
        const dateKey = cursor.format('YYYY-MM-DD');
        labels.push(cursor.format('DD MMM'));
        totals.push(totalsByDate.get(dateKey) || 0);
        cursor.add(1, 'day');
      }

      this.posMonthlySalesChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: this.$t('pos_sales_this_month'),
            data: totals,
            borderColor: '#6f42c1',
            backgroundColor: 'rgba(111, 66, 193, 0.1)',
            borderWidth: 2,
            pointBackgroundColor: '#6f42c1',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: true,
            lineTension: 0.25
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{
              gridLines: { display: false },
              ticks: { maxTicksLimit: 10 }
            }],
            yAxes: [{
              gridLines: { color: 'rgba(108, 117, 125, 0.12)' },
              ticks: {
                beginAtZero: true,
                callback: value => this.hideDashboardValues ? '******' : helper.formatMoney(value)
              }
            }]
          },
          tooltips: {
            callbacks: {
              label: tooltipItem => this.hideDashboardValues
                ? ' ******'
                : ` ${helper.formatMoney(tooltipItem.yLabel || 0)}`
            }
          }
        }
      });
    },
    async getOutOfStockProducts(page = 1){
      this.loadingOutOfStock = true;
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/products?out_of_stock=true&per_page=10&page=${page}`);
        this.products = response.data.data
        this.totalProducts = response.data.count;
        this.currentProductPage = page;
      } finally {
        this.loadingOutOfStock = false;
      }
    },
    async getInvoices(page = 1){
      const startDate = this.getStartDate(this.durationInvoices);
      const endDate = this.getEndDate(this.durationInvoices);
      const response = await helper.customGet(`${process.env.VUE_APP_API}/invoices?per_page=10&page=${page}&start=${startDate}&end=${endDate}`);
      this.invoices = response.data.data;
      this.totalTransactions = response.data.count;
      this.currentTransactionPage = page;
    },
    outstandingAmount(inv) {
      const total = parseFloat(inv.total || 0);
      const paid = parseFloat(inv.amount_paid || 0);
      return Math.max(0, total - paid);
    },
    async fetchDueInvoices() {
      this.loadingDueInvoices = true;
      try {
        // Fetch pending and partially_paid invoices within selected duration for due insights
        const startDate = this.getStartDate(this.durationInvoices);
        const endDate = this.getEndDate(this.durationInvoices);

        const pendingReq = helper.customGet(`${process.env.VUE_APP_API}/invoices?summary=true&per_page=100&page=1&status=pending&start=${startDate}&end=${endDate}`);
        const partialReq = helper.customGet(`${process.env.VUE_APP_API}/invoices?summary=true&per_page=100&page=1&status=partially_paid&start=${startDate}&end=${endDate}`);
        const overdueReq = helper.customGet(`${process.env.VUE_APP_API}/invoices?summary=true&per_page=100&page=1&status=overdue&start=${startDate}&end=${endDate}`);

        const [pendingRes, partialRes, overdueRes] = await Promise.allSettled([pendingReq, partialReq, overdueReq]);
        const list = [];
        if (pendingRes.status === 'fulfilled' && pendingRes.value?.data?.data) list.push(...pendingRes.value.data.data);
        if (partialRes.status === 'fulfilled' && partialRes.value?.data?.data) list.push(...partialRes.value.data.data);
        if (overdueRes.status === 'fulfilled' && overdueRes.value?.data?.data) list.push(...overdueRes.value.data.data);

        // Filter those that actually have outstanding or are explicitly overdue
        const withDue = list.filter(inv => this.outstandingAmount(inv) > 0 || inv.status === 'overdue');

        // keep only those due today or in next 7 days or overdue
        const todayStart = moment().startOf('day');
        const in7 = moment().add(7, 'days').endOf('day');
        const filtered = withDue.filter(inv => {
          if (inv.status === 'overdue') return true;
          if (!inv.due_date) return false;
          const due = moment(inv.due_date);
          return due.isBefore(todayStart) || due.isBetween(todayStart, in7, null, '[]') || due.isSame(todayStart, 'day');
        });

        // sort by due_date asc
        this.dueInvoices = filtered.sort((a,b) => moment(a.due_date).valueOf() - moment(b.due_date).valueOf());

        this.computeDueStats(withDue);
      } finally {
        this.loadingDueInvoices = false;
      }
    },
    async fetchAnalyticsInvoices() {
      this.loadingPaymentBreakdown = true;
      try {
        // Aggregate invoices for AR and Top Products within durationInvoices
        const startDate = this.getStartDate(this.durationInvoices);
        const endDate = this.getEndDate(this.durationInvoices);
        const res = await helper.customGet(`${process.env.VUE_APP_API}/invoices?summary=true&per_page=100&page=1&start=${startDate}&end=${endDate}`);
        const rows = res?.data?.data || [];
        this.computeARStats(rows);
        this.computeCollectionStats(rows);
        this.computePaymentBreakdown(rows);
      } finally {
        this.loadingPaymentBreakdown = false;
      }
    },
    computeARStats(invoices) {
      if (!Array.isArray(invoices) || !invoices.length) {
        this.arStats = { totalAR: 0, avgInvoiceValue: 0, paidRate: 0 };
        return;
      }
      let total = 0;
      let totalPaid = 0;
      let count = invoices.length;
      let ar = 0;
      for (const inv of invoices) {
        const t = parseFloat(inv.total || 0);
        const p = parseFloat(inv.amount_paid || 0);
        total += t;
        totalPaid += Math.min(t, p);
        if (['pending', 'partially_paid', 'overdue'].includes(inv.status)) {
          ar += Math.max(0, t - p);
        }
      }
      const avg = count ? (total / count) : 0;
      const paidRate = total > 0 ? (totalPaid / total) * 100 : 0;
      this.arStats = { totalAR: ar, avgInvoiceValue: avg, paidRate };
    },
    computeCollectionStats(invoices) {
      let totalPaidAmount = 0;
      const totalInvoicesCount = invoices.length || 0;
      for (const inv of invoices) {
        const t = parseFloat(inv.total || 0);
        const p = parseFloat(inv.amount_paid || 0);
        totalPaidAmount += Math.max(0, Math.min(t, p));
      }
      this.collectionStats = { totalPaidAmount, totalInvoicesCount };
    },
    computePaymentBreakdown(invoices) {
      const totals = new Map();
      let grand = 0;
      for (const inv of invoices) {
        // consider only collected amounts
        const p = Math.max(0, Math.min(parseFloat(inv.total || 0), parseFloat(inv.amount_paid || 0)));
        const method = inv.payment_method || 'unknown';
        if (p <= 0) continue;
        totals.set(method, (totals.get(method) || 0) + p);
        grand += p;
      }
      const rows = Array.from(totals.entries()).map(([method, amount]) => ({ method, amount, percent: grand > 0 ? (amount / grand) * 100 : 0 }));
      this.paymentBreakdown = rows.sort((a,b) => b.amount - a.amount);
    },
    // ===== Supplier Invoices Overview =====
    async fetchSupplierOverview() {
      this.loadingSupplierDueInvoices = true;
      this.loadingSupplierPaymentBreakdown = true;
      const startDate = this.getStartDate(this.durationPurchases, this.customPurchaseRange);
      const endDate = this.getEndDate(this.durationPurchases, this.customPurchaseRange);

      try {
        await Promise.all([
          this.fetchSupplierInvoicesAnalytics(startDate, endDate),
          this.fetchSupplierDueInvoices(startDate, endDate),
        ]);
      } finally {
        this.loadingSupplierDueInvoices = false;
        this.loadingSupplierPaymentBreakdown = false;
      }
    },
    async fetchSupplierInvoicesAnalytics(startDate, endDate) {
      try {
        const url = `${process.env.VUE_APP_API}/supplier-invoices?summary=true&per_page=100&page=1&start=${startDate}&end=${endDate}`;
        const res = await helper.customGet(url);
        const rows = res?.data?.data || [];
        this.supplierStatusTrendRows = this.buildSupplierStatusTrendRows(rows);
        // Totals and counts
        let gross = 0;
        let paidCount = 0;
        let pendingCount = 0;
        for (const inv of rows) {
          const status = inv.status;
          if (status === 'canceled' || status === 'draft') continue;
          const t = parseFloat(inv.total || 0);
          gross += t;
          if (status === 'paid') paidCount += 1;
          if (status === 'pending' || status === 'partially_paid') pendingCount += 1;
        }
        this.totalPurchasesGross = gross;
        this.totalPaidSupplierInvoices = paidCount;
        this.totalPendingSupplierInvoices = pendingCount;
        // Payment breakdown
        this.computeSupplierPaymentBreakdown(rows);
        this.$nextTick(() => {
          this.renderDashboardCharts();
        });
      } catch (e) {
        this.totalPurchasesGross = 0;
        this.totalPaidSupplierInvoices = 0;
        this.totalPendingSupplierInvoices = 0;
        this.supplierStatusTrendRows = [];
        this.supplierPaymentBreakdown = [];
      }
    },
    async fetchSupplierDueInvoices(startDate, endDate) {
      try {
        const pendingReq = helper.customGet(`${process.env.VUE_APP_API}/supplier-invoices?summary=true&per_page=100&page=1&status=pending&start=${startDate}&end=${endDate}`);
        const partialReq = helper.customGet(`${process.env.VUE_APP_API}/supplier-invoices?summary=true&per_page=100&page=1&status=partially_paid&start=${startDate}&end=${endDate}`);
        const overdueReq = helper.customGet(`${process.env.VUE_APP_API}/supplier-invoices?summary=true&per_page=100&page=1&status=overdue&start=${startDate}&end=${endDate}`);
        const [pendingRes, partialRes, overdueRes] = await Promise.allSettled([pendingReq, partialReq, overdueReq]);
        const list = [];
        if (pendingRes.status === 'fulfilled' && pendingRes.value?.data?.data) list.push(...pendingRes.value.data.data);
        if (partialRes.status === 'fulfilled' && partialRes.value?.data?.data) list.push(...partialRes.value.data.data);
        if (overdueRes.status === 'fulfilled' && overdueRes.value?.data?.data) list.push(...overdueRes.value.data.data);
        // with due
        const withDue = list.filter(inv => (inv.status === 'overdue' || (inv.due_date && (parseFloat(inv.total || 0) - parseFloat(inv.amount_paid || 0)) > 0)))
          .sort((a, b) => {
            const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
            const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
            return dateA - dateB;
          });
        this.supplierDueInvoices = withDue;
        this.computeSupplierDueStats(withDue);
      } catch (e) {
        this.supplierDueInvoices = [];
        this.supplierDueStats = { overdueAmount: 0, overdueCount: 0, dueTodayAmount: 0, dueTodayCount: 0, due7Amount: 0, due7Count: 0 };
      }
    },
    computeSupplierPaymentBreakdown(invoices) {
      const totals = new Map();
      let grand = 0;
      for (const inv of invoices) {
        const amount = Math.max(0, Math.min(parseFloat(inv.total || 0), parseFloat(inv.amount_paid || 0)));
        const method = inv.payment_method || 'unknown';
        if (amount <= 0) continue;
        totals.set(method, (totals.get(method) || 0) + amount);
        grand += amount;
      }
      const rows = Array.from(totals.entries()).map(([method, amount]) => ({ method, amount, percent: grand > 0 ? (amount / grand) * 100 : 0 }));
      this.supplierPaymentBreakdown = rows.sort((a,b) => b.amount - a.amount);
    },
    computeSupplierDueStats(invoices) {
      const todayStart = moment().startOf('day');
      const todayEnd = moment().endOf('day');
      const in7 = moment().add(7, 'days').endOf('day');
      let overdueAmount = 0, overdueCount = 0;
      let dueTodayAmount = 0, dueTodayCount = 0;
      let due7Amount = 0, due7Count = 0;
      for (const inv of invoices) {
        const outstanding = Math.max(0, parseFloat(inv.total || 0) - parseFloat(inv.amount_paid || 0));
        if (inv.status === 'overdue') { overdueAmount += outstanding; overdueCount += 1; continue; }
        if (outstanding <= 0) continue;
        if (!inv.due_date) continue;
        const due = moment(inv.due_date);
        if (due.isBefore(todayStart)) { overdueAmount += outstanding; overdueCount += 1; continue; }
        if (due.isBetween(todayStart, todayEnd, undefined, '[]')) { dueTodayAmount += outstanding; dueTodayCount += 1; }
        if (due.isAfter(todayEnd) && due.isSameOrBefore(in7)) { due7Amount += outstanding; due7Count += 1; }
      }
      this.supplierDueStats = { overdueAmount, overdueCount, dueTodayAmount, dueTodayCount, due7Amount, due7Count };
    },
    outstandingAmountSupplier(inv) {
      const total = parseFloat(inv.total || 0);
      const paid = parseFloat(inv.amount_paid || 0);
      return Math.max(0, total - paid);
    },
    computeDueStats(invoices) {
      const todayStart = moment().startOf('day');
      const todayEnd = moment().endOf('day');
      const in7 = moment().add(7, 'days').endOf('day');

      let overdueAmount = 0, overdueCount = 0;
      let dueTodayAmount = 0, dueTodayCount = 0;
      let due7Amount = 0, due7Count = 0;

      for (const inv of invoices) {
        const outstanding = this.outstandingAmount(inv);
        if (outstanding <= 0 && inv.status !== 'overdue') continue;

        if (inv.status === 'overdue') {
          overdueAmount += outstanding; overdueCount += 1;
          continue;
        }

        if (!inv.due_date) continue;
        const due = moment(inv.due_date);

        if (due.isBefore(todayStart)) {
          overdueAmount += outstanding; overdueCount += 1;
        } else if (due.isBetween(todayStart, todayEnd, null, '[]')) {
          dueTodayAmount += outstanding; dueTodayCount += 1;
        }
        if (due.isBetween(todayStart, in7, null, '[]')) {
          due7Amount += outstanding; due7Count += 1;
        }
      }

      this.dueStats = { overdueAmount, overdueCount, dueTodayAmount, dueTodayCount, due7Amount, due7Count };
    },
    getStartDate(timeRange, customRange = this.customRange) {
      switch (timeRange) {
        case 'today':
          return moment().format('YYYY-MM-DD');
        case 'yesterday':
          return moment().subtract(1, 'days').format('YYYY-MM-DD');
        case 'last_7days':
          return moment().subtract(7, 'days').format('YYYY-MM-DD');
        case 'last_month':
          return moment().subtract(1, 'month').format('YYYY-MM-DD');
        case 'last_3months':
          return moment().subtract(3, 'months').format('YYYY-MM-DD');
        case 'last_6months':
          return moment().subtract(6, 'months').format('YYYY-MM-DD');
        case 'this_year':
          return moment().startOf('year').format('YYYY-MM-DD');
        case 'last_year':
          return moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
        case 'custom':
          if (customRange && customRange.length === 2 && customRange[0]) {
            return moment(customRange[0]).format('YYYY-MM-DD');
          }
          return moment().subtract(30, 'days').format('YYYY-MM-DD');
        default:
          return moment().subtract(30, 'days').format('YYYY-MM-DD');
      }
    },
    getEndDate(timeRange, customRange = this.customRange) {
      switch (timeRange) {
        case 'yesterday':
          return moment().subtract(1, 'days').format('YYYY-MM-DD');
        case 'last_year':
          return moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
        case 'custom':
          if (customRange && customRange.length === 2 && customRange[1]) {
            return moment(customRange[1]).format('YYYY-MM-DD');
          }
          return moment().format('YYYY-MM-DD');
        default:
          return moment().format('YYYY-MM-DD');
      }
    },
    async updateSingleMetric(metric, duration) {
      const value = await this.fetchMetric(metric, duration);
      
      switch(metric) {
        case 'total_sales':
          this.salesMetrics.today = value;
          break;
        case 'total_revenues':
          this.salesMetrics.week = value;
          break;
        case 'total_expenses':
          this.salesMetrics.month = value;
          break;
      }
    },
    async initializeMetrics() {
      this.loadingInventoryStats = true;
      this.fetchPosOverview();
      const summaryLoaded = await this.fetchDashboardSummary();
      if (!summaryLoaded) {
        const [products, outOfStock] = await Promise.all([
          this.fetchMetric('total_products'),
          this.fetchMetric('total_out_of_stock')
        ]);
        this.inventoryStats = {
          totalProducts: products,
          outOfStock: outOfStock
        };
        await Promise.allSettled([
          this.getTotalPaidInvoies('paid'),
          this.getTotalPaidInvoies('pending'),
          this.getTotalPaidInvoies('canceled'),
          this.getTotalPaidInvoies('partially_paid'),
          this.updateSingleMetric('total_sales', this.durationTotalSales)
        ]);
      }

      this.fetchPaidInvoicesTrend()
      this.getOutOfStockProducts()
      this.loadingInventoryStats = false;

      // Load due invoices insights
      await this.fetchDueInvoices();

      // Load sales and purchase analytics using their own selected periods
      await Promise.all([
        this.fetchAnalyticsInvoices(),
        this.fetchSupplierOverview(),
      ]);
      
      // Render inventory chart
      this.$nextTick(() => {
        this.renderInventoryChart();
      });
    },
    formatDate(date) { return helper.formatDate(date) },
    renderInventoryChart() {
      if (!this.$refs.inventoryChart) return;
      
      const ctx = this.$refs.inventoryChart.getContext('2d');
      
      // Destroy existing chart if it exists
      if (this.inventoryChart) {
        this.inventoryChart.destroy();
      }
      
      const inStock = this.inventoryStats.totalProducts - this.inventoryStats.outOfStock;
      const outOfStock = this.inventoryStats.outOfStock;
      
      this.inventoryChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: [this.$t('in_stock'), this.$t('out_of_stock')],
          datasets: [{
            data: [inStock, outOfStock],
            backgroundColor: ['#28a745', '#dc3545'],
            borderWidth: 3,
            borderColor: '#fff',
            hoverBorderWidth: 4,
            hoverBorderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          cutoutPercentage: 70,
          legend: {
            display: false
          },
          tooltips: {
            enabled: true,
            backgroundColor: 'rgba(0,0,0,0.8)',
            titleFontSize: 13,
            bodyFontSize: 12,
            cornerRadius: 6,
            displayColors: true,
            callbacks: {
              label: (tooltipItem, data) => {
                const label = data.labels[tooltipItem.index];
                if (this.hideDashboardValues) {
                  return ' ' + label + ': ******';
                }
                const value = data.datasets[0].data[tooltipItem.index];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return ' ' + label + ': ' + value + ' (' + percentage + '%)';
              }
            }
          },
          animation: {
            animateRotate: false,
            animateScale: false,
            duration: 0
          },
          hover: { animationDuration: 0 },
          responsiveAnimationDuration: 0
        }
      });
    },
    renderDashboardCharts() {
      const invoiceRows = this.normalizeInvoiceTrendRows(this.invoiceStatusTrendRows);
      this.renderMiniLineChart('totalSalesChart', 'totalSalesChart', invoiceRows, 'total_amount', '#303030', this.$t('total_sales'));
      this.renderMiniLineChart('paidInvoicesChart', 'paidInvoicesChart', invoiceRows, 'paid_amount', '#28a745', this.$t('paid_invoices'));
      this.renderMiniLineChart('pendingInvoicesChart', 'pendingInvoicesChart', invoiceRows, 'pending_amount', '#6c757d', this.$t('pending_invoices'));
      this.renderMiniLineChart('partiallyPaidInvoicesChart', 'partiallyPaidInvoicesChart', invoiceRows, 'partially_paid_amount', '#ffc107', this.$t('partially_invoices'));
      this.renderMiniLineChart('overdueInvoicesChart', 'overdueInvoicesChart', invoiceRows, 'overdue_amount', '#dc3545', this.$t('overdue_invoices'));
      this.renderMiniLineChart('canceledInvoicesChart', 'canceledInvoicesChart', invoiceRows, 'canceled_amount', '#dc3545', this.$t('canceled_invoices'));

      const supplierRows = this.supplierStatusTrendRows || [];
      this.renderMiniLineChart('totalPurchasesChart', 'totalPurchasesChart', supplierRows, 'total_amount', '#303030', this.$t('total_purchases'));
      this.renderMiniLineChart('paidSupplierInvoicesChart', 'paidSupplierInvoicesChart', supplierRows, 'paid_amount', '#28a745', this.$t('paid_supplier_invoices'));
      this.renderMiniLineChart('pendingSupplierInvoicesChart', 'pendingSupplierInvoicesChart', supplierRows, 'pending_amount', '#6c757d', this.$t('pending_supplier_invoices'));
      this.renderMiniLineChart('overdueSupplierInvoicesChart', 'overdueSupplierInvoicesChart', supplierRows, 'overdue_amount', '#dc3545', this.$t('overdue_invoices'));
    },
    normalizeInvoiceTrendRows(rows = []) {
      if (!Array.isArray(rows)) return [];
      return rows.map(row => {
        const paid = parseFloat(row.paid_amount || 0);
        const pending = parseFloat(row.pending_amount || 0);
        const partiallyPaid = parseFloat(row.partially_paid_amount || 0);
        const overdue = parseFloat(row.overdue_amount || 0);
        const canceled = parseFloat(row.canceled_amount || 0);
        return {
          ...row,
          paid_amount: paid,
          pending_amount: pending,
          partially_paid_amount: partiallyPaid,
          overdue_amount: overdue,
          canceled_amount: canceled,
          total_amount: paid + pending + partiallyPaid + overdue
        };
      });
    },
    buildSupplierStatusTrendRows(invoices = []) {
      const rowsByDate = new Map();
      invoices.forEach(invoice => {
        if (invoice.status === 'draft' || invoice.status === 'canceled') return;
        const date = moment(invoice.created_at || invoice.date).format('YYYY-MM-DD');
        if (!rowsByDate.has(date)) {
          rowsByDate.set(date, {
            date,
            paid_amount: 0,
            pending_amount: 0,
            overdue_amount: 0,
            total_amount: 0
          });
        }

        const row = rowsByDate.get(date);
        const total = parseFloat(invoice.total || 0);
        const outstanding = Math.max(0, total - parseFloat(invoice.amount_paid || 0));
        row.total_amount += total;

        if (invoice.status === 'paid') {
          row.paid_amount += total;
        } else if (invoice.status === 'overdue' || (invoice.due_date && moment(invoice.due_date).isBefore(moment(), 'day') && outstanding > 0)) {
          row.overdue_amount += outstanding || total;
        } else if (invoice.status === 'pending' || invoice.status === 'partially_paid') {
          row.pending_amount += outstanding || total;
        }
      });

      return Array.from(rowsByDate.values()).sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
    },
    renderMiniLineChart(refName, chartKey, rows = [], valueKey, color, label) {
      const canvasRef = this.$refs[refName];
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
      if (!canvas) return;

      if (this.dashboardCharts[chartKey]) {
        this.dashboardCharts[chartKey].destroy();
      }

      const labels = Array.isArray(rows) && rows.length ? rows.map(row => moment(row.date).format('DD MMM')) : [label];
      const values = Array.isArray(rows) && rows.length ? rows.map(row => parseFloat(row[valueKey] || 0)) : [0];
      const hasValues = values.some(value => value > 0);
      const ctx = canvas.getContext('2d');

      this.$set(this.dashboardCharts, chartKey, new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label,
            data: values,
            borderColor: color,
            backgroundColor: hasValues ? this.hexToRgba(color, 0.14) : 'rgba(108, 117, 125, 0.08)',
            pointRadius: hasValues ? 2 : 0,
            pointHoverRadius: 3,
            borderWidth: 2,
            fill: true,
            lineTension: 0.35
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 0 },
          hover: { animationDuration: 0 },
          responsiveAnimationDuration: 0,
          legend: { display: false },
          tooltips: {
            enabled: hasValues,
            callbacks: {
              label: (tooltipItem) => this.formatMoney(tooltipItem.yLabel || 0)
            }
          },
          scales: {
            yAxes: [{
              display: false,
              ticks: {
                beginAtZero: true,
                suggestedMax: hasValues ? undefined : 1
              },
              gridLines: { display: false }
            }],
            xAxes: [{
              display: false,
              gridLines: { display: false }
            }]
          },
          layout: {
            padding: { top: 4, right: 2, bottom: 0, left: 2 }
          }
        }
      }));
    },
    hexToRgba(hex, alpha) {
      const normalized = hex.replace('#', '');
      const bigint = parseInt(normalized, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    getPaymentMethodColor(method) {
      const colors = {
        'cash': 'bg-success',
        'card': 'bg-primary',
        'credit_card': 'bg-primary',
        'bank_transfer': 'bg-info',
        'check': 'bg-warning',
        'cheque': 'bg-warning',
        'mobile_money': 'bg-purple',
        'paypal': 'bg-info',
        'stripe': 'bg-primary',
        'unknown': 'bg-secondary'
      };
      return colors[method] || 'bg-secondary';
    }
  },
  watch: {
    customRange(newValue) {
      if (this.durationTotalSales === 'custom' && newValue && newValue.length === 2) {
        this.refreshDashboardSummary()
        this.fetchPaidInvoicesTrend()
      }
      if (this.durationInvoices === 'custom' && newValue && newValue.length === 2) {
        this.fetchDueInvoices();
        this.fetchAnalyticsInvoices();
      }
    },
    durationTotalSales() {
      this.refreshDashboardSummary()
      this.fetchPaidInvoicesTrend()
    },
    durationInvoices() {
      this.fetchDueInvoices();
      this.fetchAnalyticsInvoices();
    },
    customPurchaseRange(newValue) {
      if (this.durationPurchases === 'custom' && newValue && newValue.length === 2 && newValue[0] && newValue[1]) {
        this.fetchSupplierOverview();
      }
    },
    durationPurchases(newValue) {
      if (newValue !== 'custom' || (this.customPurchaseRange && this.customPurchaseRange[0] && this.customPurchaseRange[1])) {
        this.fetchSupplierOverview();
      }
    },
    'business.metadata': {
      handler() {
        this.loadDashboardSettings();
      },
      deep: true
    }
  },
  computed : {
        business(){ return this.$store.getters.BUSINESS},
        allAvailableInsightsHidden() {
          const availableInsights = [
            {
              permission: 'view_inventories',
              setting: 'show_products_overview'
            },
            {
              permission: 'view_pos',
              setting: 'show_pos_overview'
            },
            {
              permission: 'view_invoices',
              setting: 'show_sales_overview'
            },
            {
              permission: 'view_purchases',
              setting: 'show_purchases_overview'
            }
          ].filter(insight => helper.checkAccess(insight.permission) !== -1);

          return availableInsights.length > 0
            && availableInsights.every(insight => !this.dashboardSettings[insight.setting]);
        },
        hideDashboardValues() {
          return this.dashboardSettings.hide_values === true;
        },
        posOverviewCards() {
          return [
            {
              key: 'today',
              label: 'pos_sales_today',
              icon: 'bi bi-calendar-day',
              color: '#198754',
              backgroundColor: 'rgba(25, 135, 84, 0.1)'
            },
            {
              key: 'week',
              label: 'pos_sales_this_week',
              icon: 'bi bi-calendar-week',
              color: '#0d6efd',
              backgroundColor: 'rgba(13, 110, 253, 0.1)'
            },
            {
              key: 'month',
              label: 'pos_sales_this_month',
              icon: 'bi bi-calendar3',
              color: '#6f42c1',
              backgroundColor: 'rgba(111, 66, 193, 0.1)'
            }
          ];
        },
        helper() { return helper; },
        modules(){ return this.$store.getters.MODULES },
  },
  mounted() {
    this.loadDashboardSettings();
    this.initializeMetrics();
  },
  beforeDestroy() {
    if (this.inventoryChart) {
      this.inventoryChart.destroy();
    }
    if (this.posMonthlySalesChart) {
      this.posMonthlySalesChart.destroy();
    }
    Object.values(this.dashboardCharts || {}).forEach(chart => {
      if (chart) chart.destroy();
    });
  }
}
</script>

<style scoped>
.dashboard-empty-insights-card {
  border: 1px solid rgba(0, 0, 0, 0.125) !important;
  background: #fff !important;
}

.dashboard-empty-insights-card .card-body {
  padding: 3.5rem 1.5rem;
}

.dashboard-empty-insights-icon {
  align-items: center;
  background: #eef2ff;
  border-radius: 50%;
  color: #4a6cf7;
  display: inline-flex;
  font-size: 1.75rem;
  height: 64px;
  justify-content: center;
  width: 64px;
}

.insight-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pos-overview-card {
  border-left: 4px solid var(--pos-accent) !important;
}

.pos-overview-loading {
  align-items: center;
  display: flex;
  height: 29px;
}

.pos-monthly-chart-card {
  border: 1px solid rgba(0, 0, 0, 0.125) !important;
}

.pos-monthly-chart-icon {
  background: rgba(111, 66, 193, 0.1);
  color: #6f42c1;
}

.pos-monthly-chart-wrap,
.pos-monthly-chart-state {
  height: 280px;
}

.pos-monthly-chart-state {
  align-items: center;
  display: flex;
  justify-content: center;
}

.pos-monthly-empty-icon {
  font-size: 2.5rem;
}

.insight-icon-sm {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

.insight-icon-xs {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.dashboard-mini-chart {
  width: 100%;
  height: 58px;
  border-top: 1px solid rgba(108, 117, 125, 0.14);
  padding-top: 0.35rem;
}

.dashboard-summary-chart {
  width: 100%;
  height: 72px;
}

.dashboard-mini-chart-canvas {
  width: 100% !important;
  height: 100% !important;
}

.dashboard-mini-chart-loading {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-mini-spinner {
  width: 18px;
  height: 18px;
}

/* Payment breakdown progress bars */
.payment-breakdown .progress {
  background-color: #e9ecef;
  border-radius: 4px;
}

.payment-breakdown .badge {
  font-size: 0.7rem;
  padding: 0.25em 0.5em;
}

.bg-purple {
  background-color: #6f42c1 !important;
}

/* Compact spacing */
.row.g-3 {
  row-gap: 0.75rem !important;
}

/* Metric cards equal height */
.col-md-3.col-sm-6 {
  margin-bottom: 0.75rem;
}

/* Payment breakdown improvements */
.payment-breakdown {
  max-height: 220px;
  overflow-y: auto;
}

.payment-breakdown::-webkit-scrollbar {
  width: 4px;
}

.payment-breakdown::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.payment-breakdown::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.payment-breakdown::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Custom select styling for gradient card */
select.form-select.text-white option {
  color: #000;
  background-color: #fff;
}

/* Better card borders */
.card {
  border: 1px solid rgba(0,0,0,.125) !important;
}

/* Improve text hierarchy */
.fw-semibold {
  font-weight: 600;
}

.table th {
  border-top: none;
  color: #6c757d;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.4rem 0.5rem;
}

.table td {
  vertical-align: middle;
  font-size: 0.8rem;
  padding: 0.4rem 0.5rem;
}

.table-sm th,
.table-sm td {
  padding: 0.3rem 0.4rem;
}

.badge {
  font-weight: 500;
  padding: 0.5em 0.75em;
}
</style>
